import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, AlertTriangle, CheckCircle, XCircle, Loader2, Info, ChevronDown, ChevronUp, Shield, TrendingUp, BookOpen, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getApiBase } from '../lib/apiBase';


const API = getApiBase();

/* ── R_ACC,0 lookup: cement type -> w/c -> [mean, std] ── */
const RACC_TABLE = {
  CEM_I:      { label: 'CEM I',           wc: { 0.40:[800,240], 0.45:[1100,340], 0.50:[1577,764], 0.55:[2700,950], 0.60:[4500,1300] } },
  CEM_II_A_L: { label: 'CEM II/A-L',      wc: { 0.40:[1200,360], 0.45:[1800,560], 0.50:[2800,880], 0.55:[4000,1200], 0.60:[6200,1700] } },
  CEM_II_B_L: { label: 'CEM II/B-L',      wc: { 0.40:[1500,450], 0.45:[2200,680], 0.50:[3400,1020], 0.55:[5000,1600], 0.60:[7500,2100] } },
  CEM_III_A:  { label: 'CEM III/A (36-65% GGBS)', wc: { 0.40:[1800,550], 0.45:[3000,900], 0.50:[5300,1600], 0.55:[7800,2400], 0.60:[11000,3400] } },
  CEM_III_B:  { label: 'CEM III/B (66-80% GGBS)', wc: { 0.40:[3000,900], 0.45:[5000,1500], 0.50:[8900,2930], 0.55:[13000,4200], 0.60:[19000,6000] } },
};
const WC_OPTIONS = [0.40, 0.45, 0.50, 0.55, 0.60];

/* ── Demo: Elbe Tunnel — Gehlen (2000) ── */
const DEMO_ELBE = {
  asset_name: 'Elbe Tunnel \u2014 R\u00F6hrenabschnitt',
  element_type: 'Tunnel lining (interior)',
  assessment_horizon: 100,
  xc_class: 'XC3', bar_diameter: 16, structure_age: 30,
  cover_mean: 47.5, cover_std: 18.8,
  cement_type: 'CEM_I', wc_ratio: 0.60,
  racc_mean: 3943, racc_std: 1577, racc_custom: true,
  rh_mean: 78, rh_std: 15, rh_min: 40, rh_max: 100, rh_distribution: 'beta',
  co2_mean: 0.00082, co2_std: 0.0001,
  kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
  bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
  tow: 0, psr: 0,
  curing_days: 2, epsilon_xc_std: 2.0,
  inspection_age: 15, xc_measured_mean: 7.8, xc_measured_std: 3.5,
  prior_method: 'FORM', posterior_method: 'MCMC',
  future_rh_enabled: false, future_rh_transition_year: null,
  future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
};

/* ── Demo: Dutch Railway Tunnel — Gehlen & Sodeikat (2002), validated against paper ── */
const DEMO_DUTCH = {
  asset_name: 'Dutch Railway Tunnel \u2014 Paper Validation',
  element_type: 'Tunnel lining (interior)',
  assessment_horizon: 100,
  xc_class: 'XC3', bar_diameter: 16, structure_age: 100,
  cover_mean: 47.5, cover_std: 18.8,
  cement_type: 'CEM_I', wc_ratio: 0.60,
  racc_mean: 3943, racc_std: 1577, racc_custom: true,
  rh_mean: 78, rh_std: 15, rh_min: 40, rh_max: 100, rh_distribution: 'beta',
  co2_mean: 0.00082, co2_std: 0.0001,
  kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
  bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
  tow: 0, psr: 0,
  curing_days: 2, epsilon_xc_std: 2.0,
  inspection_age: 15, xc_measured_mean: 7.8, xc_measured_std: 3.5,
  prior_method: 'SORM', posterior_method: 'FORM',
  future_rh_enabled: false, future_rh_transition_year: null,
  future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
};

/* ── Demo: Munich Highway — Gehlen & Sodeikat (2002) ── */
const DEMO_MUNICH = {
  asset_name: 'Munich Highway Bridge \u2014 A99',
  element_type: 'Bridge deck (underside, sheltered)',
  assessment_horizon: 100,
  xc_class: 'XC3', bar_diameter: 16, structure_age: 30,
  cover_mean: 35, cover_std: 8,
  cement_type: 'CEM_I', wc_ratio: 0.50,
  racc_mean: 1577, racc_std: 764, racc_custom: false,
  rh_mean: 75, rh_std: 12, rh_min: 40, rh_max: 100, rh_distribution: 'beta',
  co2_mean: 0.00082, co2_std: 0.0001,
  kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
  bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
  tow: 0, psr: 0,
  curing_days: 7, epsilon_xc_std: 2.0,
  inspection_age: 20, xc_measured_mean: 5.5, xc_measured_std: 2.0,
  prior_method: 'FORM', posterior_method: 'MCMC',
  future_rh_enabled: false, future_rh_transition_year: null,
  future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
};

/* ── Case Study Library: Thesis validated cases (Stefanie, Tables 99-105) + existing demos ── */
const CASE_STUDY_LIBRARY = [
  { category: 'Gehlen & Sodeikat (2002)', items: [
    { id: 'dutch', label: 'Dutch Railway Tunnel', desc: 'XC3, SORM+FORM Bayesian, validated', preset: DEMO_DUTCH },
    { id: 'elbe', label: 'Elbe Tunnel', desc: 'XC3, FORM+MCMC, Hamburg', preset: DEMO_ELBE },
    { id: 'munich', label: 'Munich A99 Bridge', desc: 'XC3, FORM+MCMC, sheltered', preset: DEMO_MUNICH },
  ]},
  { category: 'Stefanie PhD Thesis (2014)', items: [
    { id: 'stef1', label: 'Nr.1 Madrid Specimens', desc: 'XC4, Hypodromo de la Zarzuela',
      preset: {
        asset_name: 'Nr.1 Madrid Specimens \u2014 Hypodromo de la Zarzuela',
        element_type: 'Test specimens (exposed)', assessment_horizon: 100,
        xc_class: 'XC4', bar_diameter: 16, structure_age: 100,
        cover_mean: 30, cover_std: 6,
        cement_type: 'CEM_I', wc_ratio: 0.50,
        racc_mean: 2145.4, racc_std: 971, racc_custom: true,
        rh_mean: 62, rh_std: 11, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00091, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0.1, psr: 0.3,
        curing_days: 2, epsilon_xc_std: 0.0,
        inspection_age: 7, xc_measured_mean: 4.9, xc_measured_std: 1.8,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef2', label: 'Nr.2 Chamine Bridge', desc: 'XC3, Montargil, Portugal',
      preset: {
        asset_name: 'Nr.2 Chamine Bridge \u2014 Montargil, Portugal',
        element_type: 'Bridge (sheltered)', assessment_horizon: 100,
        xc_class: 'XC3', bar_diameter: 16, structure_age: 100,
        cover_mean: 35, cover_std: 6,
        cement_type: 'CEM_I', wc_ratio: 0.50,
        racc_mean: 2145.4, racc_std: 971, racc_custom: true,
        rh_mean: 85, rh_std: 8, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00073, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0, psr: 0,
        curing_days: 2, epsilon_xc_std: 0.0,
        inspection_age: 60, xc_measured_mean: 11.0, xc_measured_std: 5.2,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef3', label: 'Nr.3 Veranda Columns', desc: 'XC3, Midlands, UK',
      preset: {
        asset_name: 'Nr.3 Veranda Columns \u2014 Midlands, UK',
        element_type: 'Veranda columns (sheltered)', assessment_horizon: 100,
        xc_class: 'XC3', bar_diameter: 16, structure_age: 100,
        cover_mean: 35, cover_std: 6,
        cement_type: 'CEM_III_B', wc_ratio: 0.55,
        racc_mean: 25240, racc_std: 6641.4, racc_custom: true,
        rh_mean: 82, rh_std: 10, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00083, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0, psr: 0,
        curing_days: 4, epsilon_xc_std: 0.0,
        inspection_age: 9.5, xc_measured_mean: 15.0, xc_measured_std: 6.9,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef4', label: 'Nr.4 Berlin Facade', desc: 'XC4, High-rise, Germany',
      preset: {
        asset_name: 'Nr.4 High-rise Facade \u2014 Berlin, Germany',
        element_type: 'Facade (exposed)', assessment_horizon: 100,
        xc_class: 'XC4', bar_diameter: 16, structure_age: 100,
        cover_mean: 40, cover_std: 9,
        cement_type: 'CEM_III_A', wc_ratio: 0.50,
        racc_mean: 8392.3, racc_std: 2813.6, racc_custom: true,
        rh_mean: 76, rh_std: 11, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00083, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0.2, psr: 0.1,
        curing_days: 6, epsilon_xc_std: 0.0,
        inspection_age: 6, xc_measured_mean: 8.7, xc_measured_std: 3.3,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef5', label: 'Nr.5 Aachen Stair Tower', desc: 'XC4, Germany',
      preset: {
        asset_name: 'Nr.5 Stair Tower \u2014 Aachen, Germany',
        element_type: 'Stair tower (exposed)', assessment_horizon: 100,
        xc_class: 'XC4', bar_diameter: 16, structure_age: 100,
        cover_mean: 30, cover_std: 6,
        cement_type: 'CEM_I', wc_ratio: 0.60,
        racc_mean: 7887.5, racc_std: 2680, racc_custom: true,
        rh_mean: 76, rh_std: 12, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00083, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0.2, psr: 0.1,
        curing_days: 2, epsilon_xc_std: 0.0,
        inspection_age: 30, xc_measured_mean: 11.9, xc_measured_std: 5.8,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef6', label: 'Nr.6 Bergen Facades', desc: 'XC4, Norway',
      preset: {
        asset_name: 'Nr.6 High-rise Facades \u2014 Bergen, Norway',
        element_type: 'Facades (exposed)', assessment_horizon: 100,
        xc_class: 'XC4', bar_diameter: 16, structure_age: 100,
        cover_mean: 35, cover_std: 6,
        cement_type: 'CEM_I', wc_ratio: 0.55,
        racc_mean: 4732.5, racc_std: 1799.7, racc_custom: true,
        rh_mean: 76, rh_std: 12, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00083, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0.2, psr: 0.1,
        curing_days: 4, epsilon_xc_std: 0.0,
        inspection_age: 10.2, xc_measured_mean: 5.4, xc_measured_std: 2.5,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
    { id: 'stef7', label: 'Nr.7 Oslo Shopping Centre', desc: 'XC3, Norway',
      preset: {
        asset_name: 'Nr.7 Shopping Centre \u2014 Oslo, Norway',
        element_type: 'Facade (partially sheltered)', assessment_horizon: 100,
        xc_class: 'XC3', bar_diameter: 16, structure_age: 100,
        cover_mean: 25, cover_std: 6,
        cement_type: 'CEM_I', wc_ratio: 0.55,
        racc_mean: 7887.5, racc_std: 2680.7, racc_custom: true,
        rh_mean: 77, rh_std: 11, rh_min: 40, rh_max: 100, rh_distribution: 'weibull_max',
        co2_mean: 0.00083, co2_std: 0.0001,
        kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
        bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
        tow: 0, psr: 0,
        curing_days: 5, epsilon_xc_std: 0.0,
        inspection_age: 9, xc_measured_mean: 8.9, xc_measured_std: 3.4,
        prior_method: 'SORM', posterior_method: 'FORM',
        future_rh_enabled: false, future_rh_transition_year: null,
        future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
      }},
  ]},
];

const DEFAULTS = {
  asset_name: '', element_type: 'Wall', assessment_horizon: 50,
  xc_class: 'XC3', bar_diameter: 16, structure_age: null,
  cover_mean: 35, cover_std: 8,
  cement_type: 'CEM_I', wc_ratio: 0.50,
  racc_mean: 1577, racc_std: 764, racc_custom: false,
  rh_mean: 70, rh_std: 12, rh_min: 40, rh_max: 100, rh_distribution: 'beta',
  co2_mean: 0.00082, co2_std: 0.0001,
  kt_mean: 1.25, kt_std: 0.35, et_mean: 315.5, et_std: 48.0,
  bc_mean: -0.567, bc_std: 0.024, bw_mean: 0.446, bw_std: 0.163,
  tow: 0, psr: 0,
  curing_days: 7, epsilon_xc_std: 2.0,
  inspection_age: null, xc_measured_mean: null, xc_measured_std: null,
  prior_method: 'FORM', posterior_method: 'MCMC',
  future_rh_enabled: false, future_rh_transition_year: null,
  future_rh_mean: null, future_rh_std: null, future_rh_min: 40, future_rh_max: 100,
};

/* ── SVG Chart with fixed gridlines + hover tooltip ── */
function LineChart({ data, yKey, label, color = '#15803d', secondData, secondColor = '#3b82f6', height = 240, showThreshold, thresholdVal, verticalLineX, yUnit }) {
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  if (!data || data.length === 0) return null;

  const pad = { t: 28, r: 28, b: 38, l: 56 };
  const w = 480, h = height;
  const iw = w - pad.l - pad.r, ih = h - pad.t - pad.b;

  const allVals = [...data.map(d => d[yKey]), ...(secondData || []).map(d => d[yKey])];
  const xMin = data[0].year, xMax = data[data.length - 1].year;

  // Fixed Y-axis increments
  const isPf = yKey === 'pf';
  const isMm = yUnit === 'mm';
  const rawMax = Math.max(...allVals);
  const yMin = 0;
  let majorStep, minorStep, yMax;
  if (isPf) {
    majorStep = 0.10; minorStep = 0.05;
  } else if (isMm) {
    majorStep = 5; minorStep = 1;
  } else {
    majorStep = rawMax > 3 ? 1.0 : 0.5;
    minorStep = majorStep / 2;
  }
  yMax = Math.ceil(rawMax / majorStep) * majorStep || majorStep;
  if (yMax - rawMax < majorStep * 0.15) yMax += majorStep;

  const sx = x => pad.l + ((x - xMin) / (xMax - xMin || 1)) * iw;
  const sy = y => pad.t + ih - ((y - yMin) / (yMax - yMin || 1)) * ih;
  const pts = d => d.map(p => `${sx(p.year)},${sy(p[yKey])}`).join(' ');

  // Generate gridlines
  const majorYLines = [];
  const minorYLines = [];
  for (let v = yMin; v <= yMax + minorStep * 0.01; v += minorStep) {
    const rounded = Math.round(v / minorStep) * minorStep;
    const isMajor = Math.abs(rounded / majorStep - Math.round(rounded / majorStep)) < 0.001;
    if (isMajor) majorYLines.push(rounded);
    else minorYLines.push(rounded);
  }

  const majorXStep = 10, minorXStep = 5;
  const majorXLines = [];
  const minorXLines = [];
  for (let yr = Math.ceil(xMin / minorXStep) * minorXStep; yr <= xMax; yr += minorXStep) {
    if (yr % majorXStep === 0) majorXLines.push(yr);
    else minorXLines.push(yr);
  }

  // Hover handler
  const handleMouse = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width * w;
    const hovYear = xMin + ((mx - pad.l) / iw) * (xMax - xMin);
    // Find nearest data point
    let best = null, bestDist = Infinity;
    for (const d of data) {
      const dist = Math.abs(d.year - hovYear);
      if (dist < bestDist) { bestDist = dist; best = d; }
    }
    let bestSecond = null;
    if (secondData) {
      for (const d of secondData) {
        if (d.year === best?.year) { bestSecond = d; break; }
      }
    }
    if (best && Math.abs(sx(best.year) - mx) < iw / (data.length * 0.6)) {
      setHover({ point: best, second: bestSecond, px: sx(best.year), py: sy(best[yKey]) });
    } else {
      setHover(null);
    }
  };

  const fmtVal = v => isPf ? (v * 100).toFixed(1) + '%' : isMm ? v.toFixed(1) + ' mm' : v.toFixed(2);

  return (
    <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: height }}
      onMouseMove={handleMouse} onMouseLeave={() => setHover(null)}>
      {/* Minor Y gridlines (no labels) */}
      {minorYLines.map(v => (
        <line key={`my${v}`} x1={pad.l} x2={w - pad.r} y1={sy(v)} y2={sy(v)} stroke="#f0f0f0" strokeWidth="0.5" />
      ))}
      {/* Major Y gridlines (with labels) */}
      {majorYLines.map(v => (
        <g key={`Yg${v}`}>
          <line x1={pad.l} x2={w - pad.r} y1={sy(v)} y2={sy(v)} stroke="#ddd" strokeWidth="0.7" />
          <text x={pad.l - 6} y={sy(v) + 3} textAnchor="end" fontSize="9" fill="#999">
            {isPf ? (v * 100).toFixed(0) + '%' : isMm ? v.toFixed(0) : v.toFixed(1)}
          </text>
        </g>
      ))}
      {/* Minor X gridlines (no labels) */}
      {minorXLines.map(yr => (
        <line key={`mx${yr}`} x1={sx(yr)} x2={sx(yr)} y1={pad.t} y2={h - pad.b} stroke="#f0f0f0" strokeWidth="0.5" />
      ))}
      {/* Major X gridlines (with labels) */}
      {majorXLines.map(yr => (
        <g key={`Xg${yr}`}>
          <line x1={sx(yr)} x2={sx(yr)} y1={pad.t} y2={h - pad.b} stroke="#eee" strokeWidth="0.5" />
          <text x={sx(yr)} y={h - 12} textAnchor="middle" fontSize="9" fill="#999">{yr}</text>
        </g>
      ))}
      {/* Threshold line */}
      {showThreshold && thresholdVal != null && (
        <>
          <line x1={pad.l} x2={w - pad.r} y1={sy(thresholdVal)} y2={sy(thresholdVal)} stroke="#ef4444" strokeWidth="1" strokeDasharray="4 2" />
          <text x={w - pad.r + 2} y={sy(thresholdVal) + 3} fontSize="8" fill="#ef4444">target</text>
        </>
      )}
      {/* Inspection vertical line */}
      {verticalLineX != null && verticalLineX >= xMin && verticalLineX <= xMax && (
        <>
          <line x1={sx(verticalLineX)} x2={sx(verticalLineX)} y1={pad.t} y2={h - pad.b}
            stroke="#6366f1" strokeWidth="1.5" strokeDasharray="6 3" />
          <text x={sx(verticalLineX) + 4} y={pad.t + 10} fontSize="8" fill="#6366f1" fontWeight="600">t_insp</text>
        </>
      )}
      {/* Data lines */}
      <polyline points={pts(data)} fill="none" stroke={color} strokeWidth="2" />
      {secondData && secondData.length > 0 && (
        <polyline points={pts(secondData)} fill="none" stroke={secondColor} strokeWidth="2" strokeDasharray="6 3" />
      )}
      {/* Hover crosshair + tooltip */}
      {hover && (
        <>
          <line x1={hover.px} x2={hover.px} y1={pad.t} y2={h - pad.b} stroke="#9ca3af" strokeWidth="0.7" strokeDasharray="3 2" />
          <circle cx={hover.px} cy={hover.py} r="3.5" fill={color} stroke="#fff" strokeWidth="1.5" />
          {hover.second && (
            <circle cx={hover.px} cy={sy(hover.second[yKey])} r="3.5" fill={secondColor} stroke="#fff" strokeWidth="1.5" />
          )}
          <g transform={`translate(${hover.px + (hover.px > w * 0.7 ? -90 : 8)}, ${Math.max(pad.t + 4, hover.py - 28)})`}>
            <rect x="0" y="0" width="82" height={hover.second ? 42 : 28} rx="4" fill="#1f2937" fillOpacity="0.92" />
            <text x="6" y="13" fontSize="9" fill="#d1d5db" fontWeight="600">Year {hover.point.year}</text>
            <text x="6" y="24" fontSize="9" fill={color}>{fmtVal(hover.point[yKey])}</text>
            {hover.second && <text x="6" y="36" fontSize="9" fill={secondColor}>{fmtVal(hover.second[yKey])}</text>}
          </g>
        </>
      )}
      {/* Title */}
      <text x={pad.l} y={16} fontSize="10" fontWeight="600" fill="#333">{label}</text>
    </svg>
  );
}

/* ── Reusable input ── */
function Field({ label, value, onChange, type = 'number', step, disabled, hint, suffix, className = '' }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <label className="text-xs font-semibold text-gray-600 block">{label}</label>
      <div className="flex items-center gap-1.5">
        <input type={type} value={value ?? ''} step={step || 'any'}
          onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400 transition-colors"
          data-testid={`risk-field-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        />
        {suffix && <span className="text-[11px] text-gray-400 whitespace-nowrap min-w-fit">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 mb-4 mt-2">
      <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-gray-400 whitespace-nowrap">{children}</h3>
      <div className="h-px flex-1 bg-gray-200" />
    </div>
  );
}

/* ── Status badge ── */
function StatusBadge({ status, beta, target }) {
  const pass = status === 'PASS';
  return (
    <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border-2 ${pass ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="risk-status-badge">
      {pass ? <CheckCircle className="w-6 h-6 text-green-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
      <div>
        <span className={`text-base font-bold ${pass ? 'text-green-800' : 'text-red-800'}`}>
          {pass ? 'ADEQUATE' : 'INADEQUATE'}
        </span>
        <span className="text-sm text-gray-500 ml-3">
          {beta != null ? `\u03B2 = ${beta.toFixed(2)}` : ''} {target ? `(target \u2265 ${target})` : ''}
        </span>
      </div>
    </div>
  );
}

function Metric({ label, value, unit, sub }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-5 py-4">
      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}<span className="text-sm font-normal text-gray-400 ml-1">{unit}</span></p>
      {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

/* ── Results ── */
function Results({ data }) {
  const { baseline, updated, importance_factors, propagation, summary } = data;
  const hasBayes = !!updated && updated.length > 0;
  const endBaseline = baseline[baseline.length - 1];
  const endUpdated = hasBayes ? updated[updated.length - 1] : null;
  const inspAge = summary.inspection_age;

  return (
    <div className="space-y-6" data-testid="risk-results">
      <StatusBadge status={summary.status} beta={hasBayes ? (endUpdated?.beta ?? summary.updated_beta) : summary.baseline_beta} target={summary.target_beta} />

      <div className="flex flex-wrap gap-2">
        <span className="text-[10px] font-bold tracking-wider text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">{summary.prior_method || summary.method}</span>
        <span className="text-[10px] font-bold tracking-wider text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full">{summary.xc_class || 'XC3'}</span>
        <span className="text-[10px] font-bold tracking-wider text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">Horizon: {summary.horizon}y</span>
        {hasBayes && <span className="text-[10px] font-bold tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
          {summary.update_method || 'MCMC'} at {inspAge}y
        </span>}
        {summary.sensitivity_method && <span className="text-[10px] font-bold tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
          {summary.sensitivity_method === 'Sobol' ? 'Sobol indices' : 'FORM importance'}
        </span>}
        {summary.future_rh_transition_year && <span className="text-[10px] font-bold tracking-wider text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">RH change at {summary.future_rh_transition_year}y</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Metric label="A priori pf" value={`${(summary.baseline_pf * 100).toFixed(1)}%`} sub={`\u03B2 = ${summary.baseline_beta}`} />
        {hasBayes && <Metric label="A posteriori pf" value={`${(summary.updated_pf * 100).toFixed(1)}%`} sub={`\u03B2 = ${summary.updated_beta}`} />}
        <Metric label="Effective Horizon" value={summary.horizon} unit="years" />
        <Metric label="xc at horizon" value={endBaseline.xc_mean} unit="mm" sub={hasBayes ? `Updated: ${endUpdated?.xc_mean} mm` : undefined} />
      </div>

      {/* Charts — full width, stacked (skip year 0 for pf/beta to avoid extreme values) */}
      <Card><CardContent className="pt-5">
        <LineChart data={baseline.filter(d => d.year > 0)} yKey="pf" label="Failure Probability (pf)" color="#15803d"
          secondData={updated} secondColor="#3b82f6"
          verticalLineX={hasBayes ? inspAge : null} />
        <div className="flex gap-4 mt-2 px-2">
          <span className="text-[11px] text-gray-500 flex items-center gap-1.5"><span className="w-5 h-0.5 bg-green-600 inline-block" /> A priori</span>
          {hasBayes && <span className="text-[11px] text-gray-500 flex items-center gap-1.5"><span className="w-5 h-0.5 inline-block" style={{ borderTop: '2px dashed #3b82f6' }} /> A posteriori</span>}
          {hasBayes && <span className="text-[11px] text-indigo-500 flex items-center gap-1.5"><span className="w-5 h-0.5 inline-block" style={{ borderTop: '2px dashed #6366f1' }} /> t_insp</span>}
        </div>
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <LineChart data={baseline.filter(d => d.year > 0)} yKey="beta" label="Reliability Index (\u03B2)" color="#15803d"
          secondData={updated} secondColor="#3b82f6" showThreshold thresholdVal={1.5}
          verticalLineX={hasBayes ? inspAge : null} />
      </CardContent></Card>

      <Card><CardContent className="pt-5">
        <LineChart data={baseline} yKey="xc_mean" label="Mean Carbonation Depth (mm)" color="#15803d"
          secondData={updated} secondColor="#3b82f6" yUnit="mm" />
      </CardContent></Card>

      {/* Propagation Model */}
      {propagation && propagation.applicable && (
        <Card data-testid="risk-propagation-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-gray-500" /> EHE-08 Propagation Model
          </CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Time to Cracking</p>
                <p className="text-xl font-bold text-gray-900">{propagation.tp_years}<span className="text-sm font-normal text-gray-400 ml-1">years</span></p>
                <p className="text-[10px] text-gray-400">Range: {propagation.tp_range_years[0]}&ndash;{propagation.tp_range_years[1]} yr</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">V<sub>corr</sub></p>
                <p className="text-xl font-bold text-gray-900">{propagation.vcorr_mean_um_yr}<span className="text-sm font-normal text-gray-400 ml-1">&mu;m/yr</span></p>
                <p className="text-[10px] text-gray-400">Range: {propagation.vcorr_range_um_yr[0]}&ndash;{propagation.vcorr_range_um_yr[1]}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Bar Diameter</p>
                <p className="text-xl font-bold text-gray-900">{propagation.bar_diameter_mm}<span className="text-sm font-normal text-gray-400 ml-1">mm</span></p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Trigger pf</p>
                <p className="text-xl font-bold text-gray-900">{(propagation.trigger_pf * 100).toFixed(1)}<span className="text-sm font-normal text-gray-400 ml-1">%</span></p>
              </div>
            </div>
            <div className={`rounded-lg px-4 py-3 border ${propagation.within_15yr_window ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm font-semibold ${propagation.within_15yr_window ? 'text-red-800' : 'text-green-800'}`}>
                {propagation.recommendation}
              </p>
              <p className="text-[10px] text-gray-500 mt-1">Formula: t<sub>p</sub> = (80/&phi;) &times; (cover / V<sub>corr</sub>)</p>
            </div>
          </CardContent>
        </Card>
      )}
      {propagation && !propagation.applicable && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
          Propagation model: {propagation.reason}
        </div>
      )}

      {/* Sensitivity */}
      {importance_factors && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">
            {summary.sensitivity_method === 'Sobol'
              ? `Sobol Total-Order Sensitivity at ${summary.horizon} years`
              : `FORM Importance Factors at ${summary.horizon} years`}
          </CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              {Object.entries(importance_factors).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-44 truncate">{k}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-green-600" style={{ width: `${Math.min(v, 100)}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-500 w-12 text-right">{v}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* NPV Risk Delta Teaser */}
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="risk-npv-teaser">
        <CardContent className="pt-5">
          <div className="flex items-start gap-4">
            <TrendingUp className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <p className="text-base font-bold text-gray-900">NPV Risk Delta Analysis</p>
              <p className="text-sm text-gray-600 mt-1">Compare reactive vs. preventative intervention strategies with full lifecycle costing.</p>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-white/60 rounded-lg px-3 py-2 border border-amber-100">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Reactive</p>
                  <p className="text-xs text-gray-500 mt-0.5">Wait for failure, emergency repair cost</p>
                </div>
                <div className="bg-white/60 rounded-lg px-3 py-2 border border-amber-100">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Preventative</p>
                  <p className="text-xs text-gray-500 mt-0.5">Scheduled intervention, lower unit cost</p>
                </div>
              </div>
              <p className="text-xs text-amber-700 font-semibold mt-3">Contact us for project-specific financial modelling.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Main page ── */
export function CarbonationRisk() {
  const navigate = useNavigate();
  const [params, setParams] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showCoverInput, setShowCoverInput] = useState(false);
  const [coverDataText, setCoverDataText] = useState('');
  const [coverFit, setCoverFit] = useState(null);
  const [coverFitting, setCoverFitting] = useState(false);
  const [showXcInput, setShowXcInput] = useState(false);
  const [xcDataText, setXcDataText] = useState('');
  const [xcFit, setXcFit] = useState(null);
  const [xcFitting, setXcFitting] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const set = useCallback((key, val) => {
    setParams(prev => {
      const next = { ...prev, [key]: val };
      if ((key === 'cement_type' || key === 'wc_ratio') && !next.racc_custom) {
        const ct = RACC_TABLE[next.cement_type];
        if (ct) {
          const entry = ct.wc[next.wc_ratio];
          if (entry) { next.racc_mean = entry[0]; next.racc_std = entry[1]; }
        }
      }
      return next;
    });
    setResult(null);
  }, []);

  const loadDemo = (demo) => { setParams({ ...demo }); setResult(null); setCoverFit(null); setXcFit(null); };

  const fitCover = async () => {
    setCoverFitting(true);
    try {
      const values = coverDataText.split(/[,\n\r\t]+/).map(s => s.trim()).filter(s => s).map(Number).filter(v => !isNaN(v) && v > 0);
      if (values.length < 2) { setCoverFit({ distribution: 'Error', message: 'Need at least 2 positive values' }); return; }
      const res = await fetch(`${API}/api/carbonation-risk/fit-cover`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ values }),
      });
      if (!res.ok) throw new Error('Fitting failed');
      setCoverFit(await res.json());
    } catch (e) { setCoverFit({ distribution: 'Error', message: e.message }); }
    finally { setCoverFitting(false); }
  };

  const fitXc = async () => {
    setXcFitting(true);
    try {
      const values = xcDataText.split(/[,\n\r\t]+/).map(s => s.trim()).filter(s => s).map(Number).filter(v => !isNaN(v) && v > 0);
      if (values.length < 2) { setXcFit({ distribution: 'Error', message: 'Need at least 2 positive values' }); return; }
      const res = await fetch(`${API}/api/carbonation-risk/fit-xc`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ values }),
      });
      if (!res.ok) throw new Error('Fitting failed');
      setXcFit(await res.json());
    } catch (e) { setXcFit({ distribution: 'Error', message: e.message }); }
    finally { setXcFitting(false); }
  };

  const inspectionReady = params.inspection_age != null && params.inspection_age > 0
    && params.xc_measured_mean != null && params.xc_measured_mean > 0;

  const run = async () => {
    if (!inspectionReady) {
      setError('Inspection data is required. Provide inspection age and measured carbonation depth.');
      return;
    }
    setLoading(true); setError(null);
    try {
      const body = { ...params };
      delete body.cement_type; delete body.wc_ratio; delete body.racc_custom;
      if (!body.future_rh_enabled) {
        body.future_rh_transition_year = null; body.future_rh_mean = null;
        body.future_rh_std = null; body.future_rh_min = null; body.future_rh_max = null;
      }
      const res = await fetch(`${API}/api/carbonation-risk/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Analysis failed');
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setResult(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Hero — matches engineering/calculator/carbonation template */}
      <section className="relative pt-24 pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide mb-6"
            style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}>
            <Shield className="w-3.5 h-3.5" /> Carbonation Exposure &mdash; Structural Durability
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[1.08] mb-4" data-testid="risk-page-title">
            Structural Durability Risk Assessment
          </h1>
          <p className="text-base text-neutral-400 leading-relaxed max-w-2xl">
            Inspection-informed residual life forecaster for existing reinforced concrete structures.
            Combines FORM / SORM / Monte Carlo reliability analysis with Bayesian MCMC or FORM updating
            from measured carbonation depths to quantify the probability of corrosion initiation over time.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* ── Left: All inputs ── */}
            <div className="lg:col-span-2 space-y-6">

              {/* Case Study Library */}
              <button onClick={() => setShowLibrary(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-green-700 border-2 border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                data-testid="risk-open-library">
                <BookOpen className="w-4 h-4" /> Case Study Library
              </button>

              {/* Method toggles — A Priori / A Posteriori */}
              <SectionTitle>Method</SectionTitle>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold tracking-wider uppercase text-gray-400 block mb-1.5">A Priori (baseline)</label>
                  <div className="flex gap-2">
                    {['FORM', 'SORM', 'MC'].map(m => (
                      <button key={m} onClick={() => set('prior_method', m)}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${params.prior_method === m ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`}
                        data-testid={`risk-prior-${m.toLowerCase()}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold tracking-wider uppercase text-gray-400 block mb-1.5">A Posteriori (Bayesian update)</label>
                  <div className="flex gap-2">
                    {['MCMC', 'FORM'].map(m => (
                      <button key={m} onClick={() => set('posterior_method', m)}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${params.posterior_method === m ? 'bg-indigo-700 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                        data-testid={`risk-posterior-${m.toLowerCase()}`}>
                        {m === 'MCMC' ? 'MCMC' : 'FORM Bayesian'}
                      </button>
                    ))}
                  </div>
                </div>
                {params.prior_method === 'MC' && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-700">Monte Carlo with 5% CV convergence threshold (OpenTURNS ProbabilitySimulationAlgorithm).</p>
                  </div>
                )}
                {params.prior_method === 'SORM' && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                    <Info className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-emerald-700">SORM Hohenbichler correction handles high non-linearity in the limit state function.</p>
                  </div>
                )}
                {params.posterior_method === 'FORM' && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-indigo-50 border border-indigo-100 rounded-lg">
                    <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-indigo-700">Bivariate FORM Bayesian update (Hohenbichler &amp; Rackwitz). Inspection data reduces uncertainty, allowing linear approximation.</p>
                  </div>
                )}
              </div>

              {/* Asset */}
              <SectionTitle>Asset &amp; Settings</SectionTitle>
              <Field label="Asset / Structure Name" value={params.asset_name} onChange={v => set('asset_name', v)} type="text" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Element Type" value={params.element_type} onChange={v => set('element_type', v)} type="text" />
                <Field label="Structure Age" value={params.structure_age} onChange={v => set('structure_age', v)} suffix="years" hint="Current age of the structure" />
              </div>
              <Field label="Assessment Horizon" value={params.assessment_horizon} onChange={v => set('assessment_horizon', v)} suffix="years" />

              {/* Cover */}
              <SectionTitle>Concrete Cover</SectionTitle>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Mean cover depth" value={params.cover_mean} onChange={v => set('cover_mean', v)} suffix="mm" />
                <Field label="Std. deviation" value={params.cover_std} onChange={v => set('cover_std', v)} suffix="mm" />
              </div>

              {/* Cover data fitting */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setShowCoverInput(!showCoverInput)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors"
                  data-testid="risk-cover-data-toggle">
                  <span>Fit from measured data</span>
                  {showCoverInput ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showCoverInput && (
                  <div className="p-4 space-y-3 border-t border-gray-200">
                    <p className="text-[10px] text-gray-400">Paste cover measurements (mm), one per line or comma-separated:</p>
                    <textarea value={coverDataText} onChange={e => setCoverDataText(e.target.value)} rows={4}
                      placeholder="35, 42, 38, 51, 29, 44, 47, 55, 32, 40..."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
                      data-testid="risk-cover-data-input" />
                    <button onClick={fitCover} disabled={coverFitting || !coverDataText.trim()}
                      className="w-full text-xs font-semibold text-green-700 border border-green-200 px-3 py-2 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors"
                      data-testid="risk-cover-fit-btn">
                      {coverFitting ? 'Fitting...' : 'Fit Distribution'}
                    </button>
                    {coverFit && coverFit.distribution !== 'Error' && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1" data-testid="risk-cover-fit-result">
                        <p className="text-xs font-semibold text-blue-800">{coverFit.distribution} &mdash; Best fit</p>
                        <p className="text-xs text-blue-600">{'\u03BC'} = {coverFit.mean} mm, {'\u03C3'} = {coverFit.std} mm</p>
                        {coverFit.bic != null && <p className="text-[10px] text-blue-400">BIC: {coverFit.bic} | n = {coverFit.n_samples}</p>}
                        {coverFit.all_fits && (
                          <p className="text-[10px] text-blue-400">{coverFit.all_fits.map(f => `${f.name}: ${f.bic}`).join(' | ')}</p>
                        )}
                        <button onClick={() => { set('cover_mean', coverFit.mean); set('cover_std', coverFit.std); }}
                          className="text-[10px] font-semibold text-blue-700 underline hover:no-underline mt-1"
                          data-testid="risk-cover-apply-btn">
                          Apply to model
                        </button>
                      </div>
                    )}
                    {coverFit && coverFit.distribution === 'Error' && (
                      <p className="text-xs text-red-600">{coverFit.message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Reinforcement */}
              <SectionTitle>Reinforcement</SectionTitle>
              <Field label="Bar diameter" value={params.bar_diameter} onChange={v => set('bar_diameter', v)} suffix="mm" hint="Propagation: tp = (80/&phi;) &times; (cover / Vcorr)" />

              {/* Mix Design */}
              <SectionTitle>Mix Design</SectionTitle>
              <div className="space-y-1 mb-3">
                <label className="text-xs font-semibold text-gray-600">Cement Type</label>
                <select value={params.cement_type}
                  onChange={e => { set('cement_type', e.target.value); set('racc_custom', false); }}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-colors"
                  data-testid="risk-cement-type">
                  {Object.entries(RACC_TABLE).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1 mb-3">
                <label className="text-xs font-semibold text-gray-600">Water / Cement Ratio</label>
                <div className="flex gap-2">
                  {WC_OPTIONS.map(wc => (
                    <button key={wc} onClick={() => { set('wc_ratio', wc); set('racc_custom', false); }}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${params.wc_ratio === wc ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`}
                      data-testid={`risk-wc-${wc}`}>
                      {wc.toFixed(2)}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`rounded-lg border px-4 py-3 ${params.racc_custom ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-100'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">R<sub>ACC,0</sub> {params.racc_custom ? '(custom)' : '(from table)'}</p>
                    <p className="text-lg font-bold text-gray-900 mt-0.5">
                      {params.racc_mean} <span className="text-sm font-normal text-gray-400">{'\u00B1'} {params.racc_std}</span>
                      <span className="text-[10px] text-gray-400 ml-2">(mm{'\u00B2'}/a)/(kgCO{'\u2082'}/m{'\u00B3'})</span>
                    </p>
                  </div>
                  {!params.racc_custom && (
                    <button onClick={() => set('racc_custom', true)} className="text-[10px] text-amber-700 underline hover:no-underline">Override</button>
                  )}
                </div>
              </div>

              {params.racc_custom && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="R_ACC,0 mean" value={params.racc_mean} onChange={v => set('racc_mean', v)} />
                  <Field label="R_ACC,0 std. dev." value={params.racc_std} onChange={v => set('racc_std', v)} />
                </div>
              )}

              <Field label="Curing duration" value={params.curing_days} onChange={v => set('curing_days', v)} suffix="days" />

              {/* Environment */}
              <SectionTitle>Environment</SectionTitle>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Mean RH" value={params.rh_mean} onChange={v => set('rh_mean', v)} suffix="%" />
                <Field label="RH std. dev." value={params.rh_std} onChange={v => set('rh_std', v)} suffix="%" />
              </div>
              <div>
                <label className="text-[10px] font-bold tracking-wider uppercase text-gray-400 block mb-1.5">RH Distribution</label>
                <div className="flex gap-2" data-testid="risk-rh-distribution">
                  {[['beta','Beta [40,100]'], ['weibull_max','Weibull (max)']].map(([val, lbl]) => (
                    <button key={val} onClick={() => set('rh_distribution', val)}
                      className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${params.rh_distribution === val ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'}`}
                      data-testid={`risk-rh-dist-${val}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {params.rh_distribution === 'weibull_max' ? 'Weibull(max): upper-bounded at 100%, standard in fib MC-SLD / DAfStb literature.' : 'Beta: bounded [40%, 100%], symmetric shape — default for general use.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="CO2 concentration" value={params.co2_mean} onChange={v => set('co2_mean', v)} suffix="kgCO2/m3" step="0.00001" />
                <Field label="CO2 std. dev." value={params.co2_std} onChange={v => set('co2_std', v)} step="0.00001" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="ToW (time of wetness)" value={params.tow} onChange={v => set('tow', v)} step="0.01" hint="0 = sheltered / indoor" />
                <Field label="pSR (driving rain prob.)" value={params.psr} onChange={v => set('psr', v)} step="0.01" hint="0 = no driving rain" />
              </div>

              {/* Future RH change */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => set('future_rh_enabled', !params.future_rh_enabled)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors"
                  data-testid="risk-future-rh-toggle">
                  <span>Future use change (RH profile)</span>
                  <div className={`w-9 h-5 rounded-full transition-colors relative ${params.future_rh_enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`w-3.5 h-3.5 rounded-full bg-white shadow-sm absolute top-[3px] transition-all ${params.future_rh_enabled ? 'left-[19px]' : 'left-[3px]'}`} />
                  </div>
                </button>
                {params.future_rh_enabled && (
                  <div className="p-4 space-y-3 border-t border-gray-200">
                    <p className="text-[10px] text-gray-400">Model an RH change at a future year (e.g., building repurposed, new cladding).</p>
                    <Field label="Transition year" value={params.future_rh_transition_year} onChange={v => set('future_rh_transition_year', v)} suffix="years" hint="Year when RH profile changes" />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="New RH mean" value={params.future_rh_mean} onChange={v => set('future_rh_mean', v)} suffix="%" />
                      <Field label="New RH std" value={params.future_rh_std} onChange={v => set('future_rh_std', v)} suffix="%" />
                    </div>
                  </div>
                )}
              </div>

              {/* Inspection */}
              <SectionTitle>Inspection Data (required)</SectionTitle>
              {!inspectionReady && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-3" data-testid="risk-inspection-warning">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">Inspection data is mandatory for this assessment. Enter measured carbonation depth from cores to enable the analysis.</p>
                </div>
              )}
              <Field label="Inspection age" value={params.inspection_age} onChange={v => set('inspection_age', v)} suffix="years" hint="Age of structure when cores were taken" />
              {params.inspection_age != null && params.inspection_age > 0 && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <Field label="Measured xc mean" value={params.xc_measured_mean} onChange={v => set('xc_measured_mean', v)} suffix="mm" />
                  <Field label="Measured xc std. dev." value={params.xc_measured_std} onChange={v => set('xc_measured_std', v)} suffix="mm" />
                </div>
              )}

              {/* Carbonation depth fitting */}
              {params.inspection_age != null && params.inspection_age > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button onClick={() => setShowXcInput(!showXcInput)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors"
                    data-testid="risk-xc-data-toggle">
                    <span>Fit from carbonation depth readings</span>
                    {showXcInput ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {showXcInput && (
                    <div className="p-4 space-y-3 border-t border-gray-200">
                      <p className="text-[10px] text-gray-400">Paste carbonation depth readings (mm), one per line or comma-separated:</p>
                      <textarea value={xcDataText} onChange={e => setXcDataText(e.target.value)} rows={4}
                        placeholder="5.2, 8.1, 6.4, 7.8, 9.3, 4.9, 7.0..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:ring-2 focus:ring-green-500/30 focus:border-green-500"
                        data-testid="risk-xc-data-input" />
                      <button onClick={fitXc} disabled={xcFitting || !xcDataText.trim()}
                        className="w-full text-xs font-semibold text-green-700 border border-green-200 px-3 py-2 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors"
                        data-testid="risk-xc-fit-btn">
                        {xcFitting ? 'Fitting...' : 'Fit Distribution'}
                      </button>
                      {xcFit && xcFit.distribution !== 'Error' && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1" data-testid="risk-xc-fit-result">
                          <p className="text-xs font-semibold text-blue-800">{xcFit.distribution} &mdash; Best fit</p>
                          <p className="text-xs text-blue-600">{'\u03BC'} = {xcFit.mean} mm, {'\u03C3'} = {xcFit.std} mm</p>
                          {xcFit.bic != null && <p className="text-[10px] text-blue-400">BIC: {xcFit.bic} | n = {xcFit.n_samples}</p>}
                          {xcFit.all_fits && (
                            <p className="text-[10px] text-blue-400">{xcFit.all_fits.map(f => `${f.name}: ${f.bic}`).join(' | ')}</p>
                          )}
                          <button onClick={() => { set('xc_measured_mean', xcFit.mean); set('xc_measured_std', xcFit.std); }}
                            className="text-[10px] font-semibold text-blue-700 underline hover:no-underline mt-1"
                            data-testid="risk-xc-apply-btn">
                            Apply to model
                          </button>
                        </div>
                      )}
                      {xcFit && xcFit.distribution === 'Error' && (
                        <p className="text-xs text-red-600">{xcFit.message}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Run */}
              <button onClick={run} disabled={loading || !inspectionReady}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-green-700 px-4 py-3 rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50 mt-4 shadow-sm"
                data-testid="risk-run-btn">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Running {params.prior_method} + {params.posterior_method} analysis...</> : <><BarChart3 className="w-4 h-4" /> Run Assessment</>}
              </button>
              {!inspectionReady && !loading && (
                <p className="text-[10px] text-center text-amber-600 mt-1">Provide inspection data to enable the assessment</p>
              )}

              {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3 mt-2" data-testid="risk-error">{error}</p>}
            </div>

            {/* ── Right: Results ── */}
            <div className="lg:col-span-3">
              {result ? (
                <Results data={result} />
              ) : (
                <div className="flex flex-col items-center justify-center h-80 text-gray-300 border-2 border-dashed border-gray-200 rounded-xl">
                  <BarChart3 className="w-12 h-12 mb-4" />
                  <p className="text-base font-medium">Results will appear here</p>
                  <p className="text-sm mt-1">Configure parameters and run the assessment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowLibrary(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-green-700" />
                <h2 className="text-lg font-bold text-gray-900">Case Study Library</h2>
              </div>
              <button onClick={() => setShowLibrary(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="risk-close-library">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(80vh - 64px)' }}>
              {CASE_STUDY_LIBRARY.map(cat => (
                <div key={cat.category}>
                  <h3 className="text-xs font-bold tracking-wider uppercase text-gray-400 mb-3">{cat.category}</h3>
                  <div className="grid gap-2">
                    {cat.items.map(item => (
                      <button key={item.id}
                        onClick={() => { loadDemo(item.preset); setShowLibrary(false); }}
                        className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-green-400 hover:bg-green-50/50 transition-all group"
                        data-testid={`risk-load-${item.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-800 group-hover:text-green-800">{item.label}</span>
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 group-hover:bg-green-100 group-hover:text-green-700">
                            {item.preset.xc_class}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                        <div className="flex gap-3 mt-2 text-[10px] text-gray-400">
                          <span>RH: {item.preset.rh_distribution === 'weibull_max' ? 'Weibull(max)' : 'Beta'} {item.preset.rh_mean}%</span>
                          <span>Cover: {item.preset.cover_mean}mm</span>
                          <span>Insp: {item.preset.inspection_age}yr</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CarbonationRisk;
